import {PartSelectableObject} from "./partSelOb";
import {FontHandler} from "./fontHandler";
import {TextObject} from "./selectableObjects";
import {ColorUtils} from "./colorUtils";
import {ImageDataExtremaBerechner} from "./imageDataExtremaBerechner";
import {MathUtils} from "./mathUtils";
import {CONSTANTS} from "./meta_data";
import {THREE} from "./resource/BVH.js";
import {Vector2} from "./delme_globals";
import globalsObject from "./globalsObject";
import {SelObTypes} from "./SelObTypes";
import {noop} from "../global/globalUtils";
import {EventVerteiler} from "../global/eventVerteiler";
import {SELOBEVENTNAMES} from "../global/GlobalEventnames";

export class PartSelectableObjectText extends PartSelectableObject {
    private font: string;
    private colorValue: number;
    private readonly outlineLineJoin: string;
    private outlineColorValue: number | string;
    private outlineZweiColorValue: number | string;
    private characterSpacingProzent:number
    outlineWidth: number;
    private fontSize: number;
    private value: string;
    private static readonly FAKTOR_ON_TEXTWIDTH = 3;

    constructor(params) {
        super(params);
        this.font = params.font || CONSTANTS.TEXTOBJECT_DEFAULTFONT;
        try {
            FontHandler.loadFontToDocumentViaName(this.font);
        } catch (e) {
            alert(e.message);
            this.font = CONSTANTS.TEXTOBJECT_DEFAULTFONT;
        }
        this.colorValue = parseInt(params.colorValue, 10) || CONSTANTS.DEFAULT_TEXT_COLOR;
        this.outlineColorValue = (params.outlineColorValue === "<none>" ? params.outlineColorValue : parseInt(params.outlineColorValue, 10));
        this.outlineZweiColorValue = (params.outlineZweiColorValue === "<none>" ? params.outlineZweiColorValue : parseInt(params.outlineZweiColorValue, 10));
        this.outlineWidth = params.outlineWidth || 0;
        this.fontSize = TextObject.getFontSize(params.value);
        this.value = params.value;
        this.handles = this.createHandles();
        this.type = SelObTypes.TEXT;
        this.outlineLineJoin = params.outlineLineJoin || CONSTANTS.TEXTOBJECT_OUTLINE_SHAPE;
        this.characterSpacingProzent = params.characterSpacingProzent || 0;
    }

    /**
     * Aus der Datenbank bekommen wir die Daten "ohne Fleisch".
     * Hier wird ausgerechnet, wo wir das Objekt hinschieben sowie den Scaling faktor, den wir anlegen müssen.
     */
    initFromDBData() {
        const versatzVectorAbsUV = new Vector2();
        this.scaling = new Vector2(1, 1);   //damit wir die Matrix erstmal berechnen können
        this.cornersInLocalCanvas = PartSelectableObjectText.calculateCornersInLocalCanvas((this.texture as THREE.Texture).image);
        this.updateCalculatedValues();
        this.setScalingFromDBRotatedRelSize();
        this.updateCalculatedValues();
        const uvdim = this.getRelUVBoundingBoxData();

        const textMiddleAbsUV = this.getAbsUVFromRelUV(uvdim.actualMiddleCoordOnPart.x, uvdim.actualMiddleCoordOnPart.y);
        versatzVectorAbsUV.x = this.translation.x - textMiddleAbsUV.x;
        versatzVectorAbsUV.y = this.translation.y - textMiddleAbsUV.y;
        this.translation.x += versatzVectorAbsUV.x;
        this.translation.y += versatzVectorAbsUV.y;
        this.getParent().parent.selObController.relTranslationCalculated(this.id, this.getRelUVFromAbsUV(this.translation));
    }

    private static createInitTaskAfterFontLoaded(selObID, font, callback) {
        if (!globalsObject.resourceManager.taskExists("initPartSelOb" + selObID)) {
            globalsObject.resourceManager.createTask("initPartSelOb" + selObID).discardAfterExecution()
                .when("fontloaded_" + font, "loaded")
                .thenDo(callback);
        }
    }

    init(afterDrawnImageCallback = noop): Promise<void> {

        return new Promise<void>((resolve) => {
            try {
                if (FontHandler.getFontLoadedStatus(this.font) === false) {
                    PartSelectableObjectText.createInitTaskAfterFontLoaded(this.id, this.font, () => this.init(afterDrawnImageCallback).then(() => resolve()));
                    return;
                }
            } catch (e) {
                alert(e.message);
                EventVerteiler.broadcast(SELOBEVENTNAMES.FONTLOADINGERROR_SET_TO_DEFAULTFONT, {font:CONSTANTS.TEXTOBJECT_DEFAULTFONT, id:this.id})
                this.font = CONSTANTS.TEXTOBJECT_DEFAULTFONT;
                this.init(afterDrawnImageCallback).then(() => resolve);
            }

            const croppedCanvas = this.getTextAsImage();
            const dimCropped = new Vector2(croppedCanvas.width, croppedCanvas.height);

            this.initTextureSizeAndMargins(dimCropped);
            (this.textureContext2D as CanvasRenderingContext2D).drawImage(croppedCanvas, this.margin.x, this.margin.y);
            if (afterDrawnImageCallback !== null) {
                afterDrawnImageCallback();
            }
            if (isNaN(this.scaling.x)) { //d.h. wir haben Scaling noch nicht, kein Klon
                this.initScaling();
            }

            this.getParent().parent.selObController.relSizeCalculated(this.id, this.getRelSize(), this.getSizeInCm());
            //Vorsicht! Die Relsize entspricht jetzt nicht mehr zwingend der Relsize aus der Datenbank!
            this.cornersInLocalCanvas = PartSelectableObjectText.calculateCornersInLocalCanvas((this.texture as THREE.Texture).image);
            this.updateCalculatedValues();
            (this.texture as THREE.Texture).needsUpdate = true;
            this.getParent().parent.utilityPainter.addSnapsForSelOb(this);
            this.handlePartTransitionOnInsert();
            this.getParent().pendingTasks.taskFinished();
            this.getParent().pendingTasks.taskFinished();
            this.viewerTaskFinished();
            resolve();
        });
    }

    private setContext2DFontData(context) {
        const doubleQuotedFont = ["", FontHandler.surroundWithSingleQuotes(this.font), ""].join("\"");
        context.font = this.fontSize + "px " + doubleQuotedFont + ", " + FontHandler.surroundWithSingleQuotes(this.font) + ", Adobe NotDef";
        context.fillStyle = "#" + this.getHexColorValue();
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.letterSpacing = PartSelectableObjectText.getLetterSpacingValueInPixel(this.characterSpacingProzent, context);
        console.log("letterSpacing: "+context.letterSpacing);
    }

    private getTextAsImage() {
        const tempImage = document.createElement("canvas") as HTMLCanvasElement;
        const tempCtx = tempImage.getContext('2d') as CanvasRenderingContext2D;
        // tempCtx.letterSpacing = (this.characterSpacingProzent/100)+"em";
        this.setContext2DFontData(tempCtx);
        this.setTempImageSizeGenerously(tempImage, this.getTextWidthAccordingToTextureContext(tempCtx));
        const insertPoint = PartSelectableObjectText.getMiddle(tempImage);
        this.setContext2DFontData(tempCtx);
        if (this.hasOutline()) {
            this.drawOutline(tempCtx, insertPoint);
        }

        tempCtx.fillText(this.value, insertPoint.x, insertPoint.y);
        return PartSelectableObjectText.getCroppedImage(tempImage, PartSelectableObjectText.calculateCornersInLocalCanvas(tempImage));
    }

    private setTempImageSizeGenerously(tempImage, textWidth) {
        PartSelectableObjectText.setCanvasDim(
            tempImage,
            Math.max(PartSelectableObjectText.FAKTOR_ON_TEXTWIDTH * textWidth, 1),
            PartSelectableObjectText.FAKTOR_ON_TEXTWIDTH * this.fontSize + (this.hasOutline() ? 2 * this.outlineWidth : 0)

        );
    }

    private static getMiddle(tempImage) {
        return new Vector2(tempImage.width / 2, tempImage.height / 2);
    }

    private getTextWidthAccordingToTextureContext(tempCtx) {
        tempCtx.letterSpacing = PartSelectableObjectText.getLetterSpacingValueInPixel(this.characterSpacingProzent, tempCtx);
        let measureText = tempCtx.measureText(this.value);
        return measureText.width + (this.hasOutline() ? 2 * this.outlineWidth : 0);
    }
    private static getLetterSpacingValueInPixel(characterSpacingPercent:number, context:CanvasRenderingContext2D, addtionalSpaceInPx:number =0):string
    {
        return ((characterSpacingPercent*PartSelectableObjectText.measureSpaceWidth(context)/100)+addtionalSpaceInPx).toString()+"px";
    }

    private static getCroppedImage(oldCanvas: HTMLCanvasElement, corners) {
        const dim = Vector2.max(new Vector2(1, 1), PartSelectableObjectText.getPixelSizeFromCorners(corners));
        const outputCanvas = document.createElement("canvas");
        PartSelectableObjectText.setCanvasDim(outputCanvas, dim.x, dim.y);
        const outputContext = outputCanvas.getContext("2d") as CanvasRenderingContext2D;
        const corner = corners[0];
        outputContext.drawImage(oldCanvas, corner.x, oldCanvas.height - corner.y, dim.x, -dim.y, 0, 0, dim.x, dim.y);
        return outputCanvas;
    }

    private static measureSpaceWidth(tempCtx:CanvasRenderingContext2D) {
        // const oldLetterSpacing = tempCtx.letterSpacing;
        // tempCtx.letterSpacing = "0";
        console.log("font: "+tempCtx.font);
        const metrics = tempCtx.measureText(' ');
        console.log("spacewidth:"+metrics.width);
        // tempCtx.letterSpacing = oldLetterSpacing;
        return metrics.width;
    }

    private static setCanvasDim(canvas: HTMLCanvasElement, width, height) {
        canvas.width = width;
        canvas.height = height;
    }

    private hasOutline() {
        return this.outlineColorValue !== "<none>";
    }

    private initTextureSizeAndMargins(dimCropped: Vector2) {
        (this.textureCanvas as HTMLCanvasElement).width = MathUtils.getNextPowerOfTwo(dimCropped.x);
        (this.textureCanvas as HTMLCanvasElement).height = MathUtils.getNextPowerOfTwo(dimCropped.y);
        this.textureSize.x = (this.textureCanvas as HTMLCanvasElement).width;
        this.textureSize.y = (this.textureCanvas as HTMLCanvasElement).height;
        this.margin.x = (this.textureSize.x - dimCropped.x) / 2;
        this.margin.y = (this.textureSize.y - dimCropped.y) / 2;
    }

    private drawOutline(context, insertCoordinate: Vector2) {
        context.lineWidth = this.outlineWidth;
        context.strokeStyle = "#" + this.getHexOutlineColorValue();
        context.lineJoin = this.outlineLineJoin;

        context.strokeText(this.value, insertCoordinate.x, insertCoordinate.y);
    }

    private initScaling() {
        if (isNaN(this.relSize.x)) {
            if (isNaN(this.DBRotatedRelSize.x)) {
                this.scaling = this.getScalingForTargetSize();
            } else {
                this.initFromDBData();
            }
        } else {
            this.setScalingFromRelSize(this.relSize);
        }
    }

    /**
     * Das textobjekt wird mit den Werten aus dem Controller-selOb selOb neu Initialisiert
     */
    reInitialize(selOb) {

        const newFontSize = TextObject.getFontSize(selOb.value);

        const fontsizeChangeFactor = newFontSize / this.fontSize;
        this.colorValue = selOb.colorValue;
        this.outlineColorValue = selOb.outlineColorValue;
        this.font = selOb.font;
        try {
            FontHandler.loadFontToDocumentViaName(this.font);
        } catch (e) {
            alert(e.message);
            this.font = CONSTANTS.TEXTOBJECT_DEFAULTFONT;
        }
        this.outlineWidth = selOb.outlineWidth;
        if (fontsizeChangeFactor !== 1 && this.hasOutline()) {
            this.outlineWidth = selOb.outlineWidth * fontsizeChangeFactor;
            this.getParent().parent.selObController.outlinePixelSizeChanged(this.id, this.outlineWidth);
        }
        this.value = selOb.value;

        this.fontSize = newFontSize;
        this.isPinned = selOb.isPinned;
        this.hasTackleTwill = selOb.hasTackleTwill;
        this.berechnungsFeature = selOb.berechnungsFeature;
        this.getParent().pendingTasks.addTasks(1);
        this.setViewerTask();
        const callback = () => this.setScalingToKeepRelHeight(selOb.relSize.y);

        this.init(callback);
    }

    static calculateCornersInLocalCanvas(canvas) {
        const ctx = canvas.getContext("2d");
        const width = canvas.width;
        const height = canvas.height;
        const imageData = ctx.getImageData(0, 0, width, height);
        const extremaBerechner = new ImageDataExtremaBerechner(imageData, width);
        const imageExtremata = extremaBerechner.getExtrema();
        return [
            new Vector2(imageExtremata.minX, height - imageExtremata.maxY), //Dieses wilde Umdrehen kommt von der Umrechnung der Koordinatenssysteme!!
            new Vector2(imageExtremata.maxX, height - imageExtremata.maxY),
            new Vector2(imageExtremata.maxX, height - imageExtremata.minY),
            new Vector2(imageExtremata.minX, height - imageExtremata.minY)
        ];
    }

    isEmpty() {
        return this.value.trim() === "";
    }

    getHexColorValue() {
        return ColorUtils.decToHex(this.colorValue);
    }

    getHexOutlineColorValue() {
        return ColorUtils.decToHex(this.outlineColorValue as number);
    }

    setScalingFromDBRotatedRelSize() {
        const uvdim = this.getRelUVBoundingBoxData();
        this.scaling.x = this.DBRotatedRelSize.x / (uvdim.width);
        this.scaling.y = this.DBRotatedRelSize.y / (uvdim.height);
    }

    public getSizeInCm() {
        const boundingBoxData = this.getRelUVBoundingBoxData();
        return new Vector2(
            boundingBoxData.width * this.parentDimensionInfos.partSizeCM.x,
            boundingBoxData.height * this.parentDimensionInfos.partSizeCM.y
        );
    }

    private static getPixelSizeFromCorners(corners) {
        return new Vector2(
            corners[CONSTANTS.CORNER_INDEX.RIGHT_BOTTOM].x - corners[CONSTANTS.CORNER_INDEX.LEFT_BOTTOM].x,
            corners[CONSTANTS.CORNER_INDEX.RIGHT_TOP].y - corners[CONSTANTS.CORNER_INDEX.RIGHT_BOTTOM].y
        );
    }
}
